@if (isFromGuide()) {
  <div class="progress-bar-container"></div>
}

<app-page-wrapper [title]="'glossRewards' | i18n">
  @if (isLoading) {
    <app-loading-overlay></app-loading-overlay>
  } @else {
    <div class="tw-flex tw-flex-col tw-w-full tw-items-center tw-py-2 sm:tw-pt-0">
      <!-- Stacks below xl, not md: with the 240px side menu, md–xl columns are too narrow. -->
      <!-- No side padding on the sections: user-layout's <main> is the one 16px gutter (#6171).
           Side by side, the 2rem gap is what the columns' own px-4 used to give. The rail below
           bleeds into that gutter (--page-gutter) so the cards scroll into it; its thumb track
           spans the rail's padded content (inset-x-0 of the wrapper). -->
      <div class="tw-flex xl:tw-flex-col tw-flex-row tw-w-full min-xl:tw-gap-8">
        <!-- LEFT: Earn Credits -->
        <div class="xl:tw-w-full min-xl:tw-flex-1">
          <!-- Mobile: stacked cards switch by tap or by swiping the front card left/right. -->
          <!-- gutter-bleed-x: reach the screen edges through <main>'s gutter (#6171). px-4 puts the
               cards back on the 16px line and is the room a dragged card slides into
               (SWIPE_MAX_OFFSET_PX); overflow-hidden clips it at the screen edge so it never widens
               the page. -->
          <div
            class="gutter-bleed-x tw-px-4 tw-overflow-hidden"
            [ngClass]="{
              'tw-hidden xs:tw-block': !isPhone,
              'tw-z-[100] tw-relative': isFromGuideViewPoints(),
            }"
          >
            <div
              class="tw-relative tw-mx-auto tw-w-full tw-max-w-[358px] tw-pt-12 tw-touch-pan-y tw-touch-pinch-zoom"
              (pointerdown)="onSwipeStart($event)"
              (pointermove)="onSwipeMove($event)"
              (pointerup)="onSwipeEnd($event)"
              (pointercancel)="onSwipeCancel($event)"
            >
              <div class="tw-aspect-[358/164]" aria-hidden="true"></div>
              <div
                class="tw-absolute tw-w-full tw-origin-top tw-cursor-pointer tw-transition-all tw-duration-300 tw-ease-in-out"
                [ngClass]="
                  isPlatinumCardFront
                    ? 'tw-top-12 tw-z-10 tw-scale-100'
                    : 'tw-top-0 tw-z-0 tw-scale-[.966]'
                "
                [style.--tw-translate-x]="isPlatinumCardFront ? swipeOffset() + 'px' : null"
                [style.transition]="swipeDragging() ? 'none' : null"
                role="button"
                tabindex="0"
                (click)="onCardTap()"
                (keydown.enter)="togglePlatinumCard()"
                (keydown.space)="$event.preventDefault(); togglePlatinumCard()"
              >
                <app-platinum-card #mobilePlatinumCard layout="stack"></app-platinum-card>
                <div
                  class="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-top-0 tw-z-10 tw-aspect-[358/164] tw-rounded-xl tw-bg-color-card-stack-dim tw-transition-opacity tw-duration-300"
                  [ngClass]="isPlatinumCardFront ? 'tw-opacity-0' : 'tw-opacity-100'"
                ></div>
              </div>

              <div
                class="tw-absolute tw-w-full tw-origin-top tw-cursor-pointer tw-transition-all tw-duration-300 tw-ease-in-out"
                [ngClass]="
                  isPlatinumCardFront
                    ? 'tw-top-0 tw-z-0 tw-scale-[.966]'
                    : 'tw-top-12 tw-z-10 tw-scale-100'
                "
                [style.--tw-translate-x]="!isPlatinumCardFront ? swipeOffset() + 'px' : null"
                [style.transition]="swipeDragging() ? 'none' : null"
                role="button"
                tabindex="0"
                (click)="onCardTap()"
                (keydown.enter)="togglePlatinumCard()"
                (keydown.space)="$event.preventDefault(); togglePlatinumCard()"
              >
                <app-total-credits-display
                  #viewPointsElement
                  #mobileTotalCreditsCard
                  layout="stack"
                  [ngClass]="{
                    'tw-z-[100] tw-relative': isFromGuideViewPoints(),
                  }"
                ></app-total-credits-display>
                <div
                  class="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-top-0 tw-z-10 tw-aspect-[358/164] tw-rounded-xl tw-bg-color-card-stack-dim tw-transition-opacity tw-duration-300"
                  [ngClass]="isPlatinumCardFront ? 'tw-opacity-100' : 'tw-opacity-0'"
                ></div>
              </div>
            </div>
          </div>
          @if (!isPhone) {
            <!-- The rail's mask-image makes it a stacking context, so for the guide the whole rail
                 has to sit above the guide mask (z-99), not just the cards inside it. -->
            <div
              class="xs:tw-hidden tw-relative tw-w-full tw--mb-3"
              [ngClass]="{ 'tw-z-[100]': isFromGuideViewPoints() }"
            >
              <div
                #balanceCardRail
                class="hide-scrollbars tw-flex tw-flex-row tw-items-start tw-gap-4 gutter-bleed-x tw-px-[var(--page-gutter,0px)] tw-pb-3 tw-overflow-x-auto"
                [attr.role]="railFadeState() === 'none' ? null : 'region'"
                [attr.aria-label]="railFadeState() === 'none' ? null : ('glossRewards' | i18n)"
                [ngClass]="{
                  'tw-cursor-grab': !railDragging(),
                  'tw-cursor-grabbing': railDragging(),
                  'tw-mask-image-[linear-gradient(to_right,#000_calc(100%-32px),transparent_100%)]':
                    railFadeState() === 'end',
                  'tw-mask-image-[linear-gradient(to_left,#000_calc(100%-32px),transparent_100%)]':
                    railFadeState() === 'start',
                  'tw-mask-image-[linear-gradient(to_right,transparent_0,#000_32px,#000_calc(100%-32px),transparent_100%)]':
                    railFadeState() === 'both',
                }"
                (scroll)="onRailScroll()"
                (wheel)="onRailWheel($event)"
                (pointerdown)="onRailPointerDown($event)"
                (mousedown)="$event.button === 0 ? $event.preventDefault() : null"
                (keydown)="onRailKeydown($event)"
              >
                <!-- Rendered in order (not CSS order) so the DOM, and so the tab order, follows
                     what the user sees after a reorder. track moves the views instead of
                     recreating them. -->
                @for (key of railCardOrder(); track key) {
                  @if (key === "platinum") {
                    <app-platinum-card
                      class="tw-relative tw-flex-shrink-0 tw-rounded-xl focus-visible:tw-outline-none focus-visible:after:tw-content-[''] focus-visible:after:tw-pointer-events-none focus-visible:after:tw-absolute focus-visible:after:tw-inset-0 focus-visible:after:tw-z-[1] focus-visible:after:tw-rounded-xl focus-visible:after:tw-outline focus-visible:after:tw-outline-2 focus-visible:after:tw-outline-color-action-primary focus-visible:after:-tw-outline-offset-2"
                      data-rail-card="platinum"
                      role="group"
                      tabindex="0"
                      [attr.aria-label]="'platinumCredits' | i18n"
                      aria-keyshortcuts="Alt+ArrowLeft Alt+ArrowRight"
                      [ngClass]="{
                        'tw-z-[100]': isFromGuideViewPoints(),
                      }"
                    ></app-platinum-card>
                  } @else {
                    <app-total-credits-display
                      #viewPointsElement
                      class="tw-relative tw-flex-shrink-0 tw-rounded-xl focus-visible:tw-outline-none focus-visible:after:tw-content-[''] focus-visible:after:tw-pointer-events-none focus-visible:after:tw-absolute focus-visible:after:tw-inset-0 focus-visible:after:tw-z-[1] focus-visible:after:tw-rounded-xl focus-visible:after:tw-outline focus-visible:after:tw-outline-2 focus-visible:after:tw-outline-color-action-primary focus-visible:after:-tw-outline-offset-2"
                      data-rail-card="gold"
                      role="group"
                      tabindex="0"
                      [attr.aria-label]="'goldCredits' | i18n"
                      aria-keyshortcuts="Alt+ArrowLeft Alt+ArrowRight"
                      [ngClass]="{
                        'tw-z-[100]': isFromGuideViewPoints(),
                      }"
                    ></app-total-credits-display>
                  }
                }
              </div>
              <span class="tw-sr-only" role="status" aria-live="polite">{{
                railReorderAnnouncement()
              }}</span>
              @if (railFadeState() !== "none") {
                <div
                  class="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-1 tw-h-1 tw-transition-opacity tw-duration-300"
                  [ngClass]="railThumbVisible() ? 'tw-opacity-100' : 'tw-opacity-0'"
                  aria-hidden="true"
                >
                  <div
                    class="tw-absolute tw-inset-y-0 tw-rounded-full tw-bg-color-scrollbar-thumb"
                    [style.left.%]="railThumb().left"
                    [style.width.%]="railThumb().width"
                  ></div>
                </div>
              }
            </div>
          }
          <!-- Single-column guide tooltip: inline below rewards cards, for viewYourCredits guide
               only. Once the columns stack, the right-column tooltip would sit below this whole
               column, far from the highlighted cards. -->
          @if (isFromGuideViewPoints()) {
            <app-task-info-tooltips
              class="min-xl:tw-hidden tw-block tw-mt-1 tw-relative tw-z-[100]"
              [tooltipLayout]="'inline'"
              [taskTitle]="'viewYourCredits'"
              [taskDescription]="'viewYourCreditsDesc'"
              [guideTitle]="guideTitle()"
              [bottomAction]="creditsGuideBottomAction()"
            ></app-task-info-tooltips>
          }
          <!-- Get More Gold Credits -->
          <div class="tw-w-full tw-mt-4 tw-mb-4">
            <div
              class="tw-flex tw-items-center tw-gap-3 sm:tw-flex-col sm:tw-items-stretch tw-shadow-card-item tw-rounded-2xl tw-p-4"
            >
              <div class="tw-flex tw-items-center tw-gap-3 tw-flex-1 tw-min-w-0">
                <div
                  class="tw-flex tw-h-10 tw-w-10 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-gradient-to-br tw-from-teal-400 tw-to-teal-600"
                >
                  <icon name="plus" [size]="5" class="tw-text-white dark:tw-text-black"></icon>
                </div>

                <div class="tw-flex tw-flex-col tw-gap-0.5 tw-flex-1 tw-min-w-0">
                  <span class="tw-font-medium tw-text-base tw-text-color-text-default tw-truncate">
                    {{ "getMoreGoldCredits" | i18n }}
                  </span>
                  <span class="tw-text-sm tw-text-color-text-tertiary tw-truncate">
                    {{ "payWithCashOrPlatinumCredits" | i18n }}
                  </span>
                </div>
              </div>

              <button
                type="button"
                (click)="onPurchaseCreditsClick('platinum')"
                class="tw-shadow-button-bg tw-w-[175px] tw-flex-shrink-0 tw-rounded-xl tw-py-2.5 tw-px-4 tw-bg-gradient-teal tw-text-center tw-text-color-text-on-action tw-font-semibold tw-text-sm sm:tw-w-full"
              >
                {{ "getCredits" | i18n }}
              </button>
            </div>
          </div>

          <!-- My Referral Code -->
          <div class="tw-w-full tw-mb-4">
            <app-collapsible-card
              [headerTitle]="'myReferralCode' | i18n"
              [showProgress]="true"
              [isExpanded]="isMyDiscountCodeExpanded"
              (isExpandedChange)="isMyDiscountCodeExpanded = $event"
            >
              <span
                header-action
                class="tw-text-xs tw-font-medium tw-text-color-text-tertiary tw-whitespace-nowrap"
              >
                {{ "friendsReferred" | i18n: referralCount }}
              </span>

              <!-- No bottom padding here: collapsible-card's expanded wrapper already adds tw-pb-5.
                   px-2 (sm:px-1) + the wrapper's px-2 (sm:px-3) lines the code box up with the
                   header's px-4 title, matching the design. -->
              <div expanded-content class="tw-px-2 sm:tw-px-1">
                <div
                  class="tw-bg-color-surface-input tw-rounded-xl tw-px-4 tw-py-6 tw-flex tw-items-center tw-justify-between tw-gap-3"
                >
                  <!-- Smaller on phones, so the code fits on one line beside Copy Code. -->
                  <span
                    class="tw-font-bold tw-text-2xl tw-min-w-0 tw-break-all sm:tw-text-lg"
                    [attr.title]="isCopied ? null : discountCode"
                    [ngClass]="
                      isCopied ? 'tw-text-color-text-action' : 'tw-text-color-text-default'
                    "
                  >
                    {{ isCopied ? ("copied" | i18n) : discountCode }}
                  </span>
                  <button
                    class="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-1.5 tw-text-color-text-tertiary tw-text-xs tw-font-semibold tw-tracking-widest tw-uppercase tw-transition-colors hover:tw-text-color-text-secondary"
                    (click)="copyDiscountCode()"
                  >
                    <span>{{ "copyCode" | i18n }}</span>
                    <icon [size]="6" [name]="'copy-06'"></icon>
                  </button>
                </div>
                <p class="tw-text-xs tw-text-color-text-tertiary tw-italic tw-mt-3">
                  {{ "shareReferralCodeHint" | i18n }}
                </p>
              </div>
            </app-collapsible-card>
          </div>

          <!-- Earn Credits Card -->
          <div class="tw-w-full tw-mb-4">
            <app-collapsible-card
              [headerTitle]="'earnCredits' | i18n"
              [showProgress]="true"
              [isExpanded]="isEarnCreditsExpanded"
              (isExpandedChange)="isEarnCreditsExpanded = $event"
            >
              <!-- Subtitle: stays visible whether the card is collapsed or expanded -->
              <span
                header-subtitle
                class="tw-block tw-text-xs tw-text-left tw-text-color-text-tertiary"
              >
                {{ "watchAdsOrExportDataBundle" | i18n }}
              </span>

              <!-- Expanded content (main content) -->
              <div expanded-content>
                <!-- Progress content (only shown when expanded AND showProgress is true) -->
                <div progress-content class="tw-flex tw-flex-col tw-gap-2 tw-mb-4">
                  <span
                    class="tw-text-[13px] tw-leading-[125%] tw-flex tw-justify-between tw-items-center tw-text-color-text-tertiary tw-gap-2"
                  >
                    <span>🎯 Watch {{ adStateService.maxAdsPerDay }} ads to earn Platinum</span>
                    <span class="tw-font-semibold tw-text-color-text-action">
                      {{ adStateService.adsWatchedToday }} / {{ adStateService.maxAdsPerDay }}
                    </span>
                  </span>

                  <div class="tw-flex tw-items-center tw-gap-1">
                    @for (status of progressArray; track $index) {
                      <div
                        class="tw-flex-1 tw-h-2 tw-rounded-full tw-transition-colors tw-duration-500"
                        [ngClass]="{
                          'tw-bg-color-money-in': status === 'complete',
                          'tw-bg-color-status-warning-icon': status === 'interrupted',
                          'tw-bg-color-border-subtle': status === 'empty',
                        }"
                      ></div>
                    }
                  </div>
                </div>

                <app-ad-list
                  (viewBundleClick)="viewBundleHistory()"
                  (createBundleClick)="createDataBundle()"
                  [isFromGuide]="isFromGuideWatchAds()"
                  [isFromGuideViewPoints]="isFromGuideViewPoints()"
                  [guideTitle]="guideTitle()"
                  [(isWatchAdTaskSucceeded)]="isWatchAdTaskSucceeded"
                  [(tryAgainLaterInTutorial)]="tryAgainLaterInTutorial"
                ></app-ad-list>
              </div>
            </app-collapsible-card>
          </div>
        </div>

        <!-- RIGHT: Redeem + Coupons -->
        <!-- -tw-mt-32: pulls the Redeem column up when the watch-ads guide is active,
             compensating for the extra height the inline tooltip adds below the highlighted ad -->
        <div
          class="xl:tw-w-full min-xl:tw-flex-1 tw-flex tw-flex-col"
          [ngClass]="{ '-tw-mt-32': isFromGuideWatchAds() }"
        >
          <div class="tw-flex tw-flex-row sm:tw-flex sm:tw-flex-col">
            @if (isFromGuideViewPoints()) {
              <app-task-info-tooltips
                class="xl:tw-hidden"
                [mobileCentered]="true"
                [taskTitle]="'viewYourCredits'"
                [taskDescription]="'viewYourCreditsDesc'"
                [guideTitle]="guideTitle()"
                [bottomAction]="creditsGuideBottomAction()"
              ></app-task-info-tooltips>
            }
          </div>
          <!-- Points Redemption (no progress bar) -->
          <div class="tw-w-full tw-mb-4">
            <app-collapsible-card
              [headerTitle]="'redeemSubscriptions' | i18n"
              [showProgress]="false"
              [isExpanded]="isRedemptionExpanded"
              (isExpandedChange)="isRedemptionExpanded = $event"
              [hideHeaderOnDesktop]="true"
            >
              <!-- Collapsed description -->
              <div collapsed-content class="tw-flex tw-flex-col tw-gap-1 tw-text-left">
                <span
                  class="tw-font-montserrat tw-font-normal tw-tracking-[0.0125em] tw-text-color-text-default"
                >
                  {{ "redeeemCreditsForDiscountsAndCoupons" | i18n }}
                </span>
                <span
                  class="tw-font-montserrat tw-font-normal tw-text-[13px] tw-leading-[125%] tw-flex tw-text-color-text-tertiary"
                >
                  1 redemption available
                </span>
              </div>

              <!-- Expanded content -->
              <div expanded-content>
                <!-- Tabs (Cash Back is beta-only, so non-beta users only have Vault Plans) -->
                @if (isBetaUser()) {
                  <div
                    class="tw-flex tw-w-full tw-p-1 tw-bg-color-segmented-container-bg tw-rounded-2xl tw-mb-4 tw-justify-between"
                  >
                    <app-tab-button
                      class="tw-flex-1"
                      [label]="'pointsRedemption' | i18n"
                      [isActive]="isVaultPlans"
                      [activeBg]="'var(--color-surface-elevated)'"
                      [segmented]="true"
                      (tabClick)="setVaultCashBackTab('vaultPlans')"
                    ></app-tab-button>

                    <app-tab-button
                      class="tw-flex-1"
                      [label]="'cashBack' | i18n"
                      [isActive]="isCashBack"
                      [activeBg]="'var(--color-surface-elevated)'"
                      [segmented]="true"
                      (tabClick)="setVaultCashBackTab('cashBack')"
                    ></app-tab-button>
                  </div>
                }

                <!-- Tab content -->
                @if (isCashBack && isBetaUser()) {
                  <app-amazon-gift-card></app-amazon-gift-card>
                } @else {
                  <app-points-redemption></app-points-redemption>
                }
              </div>
            </app-collapsible-card>
          </div>

          <!-- Coupons -->
          <div class="tw-w-full tw-mb-4">
            <app-collapsible-card
              [headerTitle]="'coupons' | i18n"
              [showProgress]="false"
              [isExpanded]="isCouponsExpanded"
              (isExpandedChange)="isCouponsExpanded = $event"
              [hideChevron]="true"
            >
              <!-- Expanded content -->
              <div expanded-content>
                <app-coupons #couponsRef></app-coupons>
              </div>

              <ng-container *ngIf="couponsRef?.hasMore()" header-action>
                <button
                  type="button"
                  (click)="couponsRef.browseAllCoupons()"
                  class="tw-w-[120px] tw-h-[40px] tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-color-surface-card tw-shadow-elevation-card tw-text-[14px] tw-font-semibold tw-text-color-text-action tw-whitespace-nowrap"
                >
                  {{ "findMore" | i18n }}
                </button>
              </ng-container>

              <!-- Collapsed description -->
              <div collapsed-content class="tw-flex tw-flex-col tw-gap-1 tw-text-left">
                <span
                  class="tw-font-montserrat tw-font-normal tw-tracking-[0.0125em] tw-text-color-text-default"
                >
                  {{ "exploreCouponOffers" | i18n }}
                </span>
              </div>
            </app-collapsible-card>
          </div>

          <!-- Recent Activity -->
          <div class="tw-w-full tw-mb-4">
            <app-collapsible-card
              [headerTitle]="'recentActivity' | i18n"
              [showProgress]="false"
              [isExpanded]="isRecentActivityExpanded"
              (isExpandedChange)="isRecentActivityExpanded = $event"
            >
              <!-- Collapsed description -->
              <div collapsed-content class="tw-flex tw-flex-col tw-gap-1 tw-text-left">
                <span
                  class="tw-font-montserrat tw-font-normal tw-tracking-[0.0125em] tw-text-color-text-default"
                >
                  {{ "viewYourCreditActivity" | i18n }}
                </span>
              </div>

              <!-- expanded content -->
              <div expanded-content>
                <app-recent-activity></app-recent-activity>
              </div>
            </app-collapsible-card>
          </div>
        </div>
      </div>
    </div>
  }
</app-page-wrapper>
